"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAttributeValuesDialogState = void 0;
const react_1 = require("react");
const graphql_1 = require("../../__generated__/graphql");
const attributes_1 = require("../client/attributes");
const values_1 = require("../client/values");
const global_tree_edit_warning_form_constants_1 = require("../components/global-tree-edit-warning/global-tree-edit-warning-form.constants");
const generic_1 = require("../generic");
const utils_1 = require("../utils");
const useAttributeValuesDialogState = ({ defaultSelectedAttributeName, selectedStrategyNodeId, catalogId, }) => {
    var _a, _b;
    const [selectedAttributeName, setSelectedAttributeName] = (0, react_1.useState)(defaultSelectedAttributeName !== null && defaultSelectedAttributeName !== void 0 ? defaultSelectedAttributeName : '');
    const [initializedAttributeName, setInitializedAttributeName] = (0, react_1.useState)();
    const [dialogInitialValues, setDialogInitialValues] = (0, react_1.useState)(Object.assign(Object.assign(Object.assign({}, generic_1.attributeInitialValues), global_tree_edit_warning_form_constants_1.globalTreeEditWarningFormInitialValues), { attributes: [] }));
    (0, react_1.useEffect)(() => {
        if (defaultSelectedAttributeName) {
            setSelectedAttributeName(defaultSelectedAttributeName);
            setInitializedAttributeName(undefined);
        }
    }, [defaultSelectedAttributeName]);
    (0, react_1.useEffect)(() => {
        if (!selectedAttributeName) {
            setInitializedAttributeName(undefined);
            setDialogInitialValues(Object.assign(Object.assign(Object.assign({}, generic_1.attributeInitialValues), global_tree_edit_warning_form_constants_1.globalTreeEditWarningFormInitialValues), { attributes: [] }));
            return;
        }
        setInitializedAttributeName(undefined);
    }, [selectedAttributeName]);
    const { data: attributesData, isLoading: attributesLoading, pagination: attributesPagination, onSearch: handleAttributeSearch, } = (0, attributes_1.useGetStrategyAttributes)({
        productTreeNodeId: selectedStrategyNodeId,
        options: { skip: !selectedStrategyNodeId },
    });
    const attributesOptionList = (0, react_1.useMemo)(() => {
        if (!(attributesData === null || attributesData === void 0 ? void 0 : attributesData.length))
            return [];
        const labelsMap = new Map();
        attributesData.forEach(({ node: { label, description } }) => {
            labelsMap.set(label, { label, value: label, description });
        });
        return (0, utils_1.sortByTextFieldCaseInsensitive)(utils_1.SortOrder.Ascend)(Array.from(labelsMap.values()), 'label');
    }, [attributesData]);
    const { data: selectedAttributeValuesData = [], isLoading: valuesLoading, attributeData: selectedAttributeData, pagination: selectedAttributeValuesPagination, refetch: refetchSelectedAttributeValues, called: valuesCalled, } = (0, values_1.useGetStrategyValues)({
        catalogId,
        attributeName: selectedAttributeName,
        productTreeNodeId: selectedStrategyNodeId,
        options: { skip: !selectedAttributeName, fetchPolicy: 'network-only' },
    });
    const dialogLoading = (!selectedAttributeName && attributesLoading)
        || (!!selectedAttributeName && initializedAttributeName !== selectedAttributeName);
    const loadedValueItems = (0, react_1.useMemo)(() => selectedAttributeValuesData.map(({ node }) => {
        var _a;
        return ({
            name: node.value,
            description: (_a = node.description) !== null && _a !== void 0 ? _a : '',
            readonly: true,
        });
    }), [selectedAttributeValuesData]);
    (0, react_1.useEffect)(() => {
        if (!selectedAttributeName
            || !valuesCalled
            || valuesLoading
            || initializedAttributeName === selectedAttributeName) {
            return;
        }
        // Query finished but returned no matching attribute (e.g. newly created, not yet server-side).
        // Resolve loading with an empty shell so the dialog doesn't stay stuck on skeleton.
        if (!(selectedAttributeData === null || selectedAttributeData === void 0 ? void 0 : selectedAttributeData.label) || selectedAttributeData.label !== selectedAttributeName) {
            setDialogInitialValues(Object.assign({ attributes: [{
                        name: selectedAttributeName,
                        description: '',
                        readonly: false,
                        values: [],
                    }] }, global_tree_edit_warning_form_constants_1.globalTreeEditWarningFormInitialValues));
            setInitializedAttributeName(selectedAttributeName);
            return;
        }
        const { label, description } = selectedAttributeData;
        setDialogInitialValues(Object.assign({ attributes: [{
                    name: label,
                    description: description || '',
                    readonly: true,
                    values: loadedValueItems,
                }] }, global_tree_edit_warning_form_constants_1.globalTreeEditWarningFormInitialValues));
        setInitializedAttributeName(selectedAttributeName);
    }, [initializedAttributeName, loadedValueItems, selectedAttributeData, selectedAttributeName, valuesCalled, valuesLoading]);
    const matchedValueNames = (0, react_1.useMemo)(() => selectedAttributeValuesData.map(({ node }) => node.value), [selectedAttributeValuesData]);
    const handleValueSearch = (0, react_1.useCallback)((searchText) => refetchSelectedAttributeValues({
        filter: (searchText === null || searchText === void 0 ? void 0 : searchText.trim())
            ? {
                some: {
                    value: {
                        contains: searchText.trim(),
                        mode: graphql_1.StringFilterMode.Insensitive,
                    },
                },
            }
            : undefined,
    }), [refetchSelectedAttributeValues]);
    return {
        selectedAttributeName,
        setSelectedAttributeName,
        dialogInitialValues,
        dialogLoading,
        attributesOptionList,
        attributesLoading,
        attributesHasNextPage: (_a = attributesPagination.hasNextPage) !== null && _a !== void 0 ? _a : false,
        attributesMoreItemsLoading: attributesPagination.isLoadingMore,
        matchedValueNames,
        loadedValueItems,
        valuesLoading,
        valuesHasNextPage: (_b = selectedAttributeValuesPagination.hasNextPage) !== null && _b !== void 0 ? _b : false,
        valuesMoreItemsLoading: selectedAttributeValuesPagination.isLoadingMore,
        handleValuesLoadMore: selectedAttributeValuesPagination.onNextPage,
        handleValueSearch,
        handleAttributesLoadMore: attributesPagination.onNextPage,
        handleAttributeSearch,
    };
};
exports.useAttributeValuesDialogState = useAttributeValuesDialogState;
